import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  MultiSelect,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowDown, IconArrowUp, IconPencil, IconRoute, IconTrash } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { trpc } from '../../lib/trpc';

/**
 * Agent routing rules — auto-assignment of inbound conversations.
 * Rules evaluate top-to-bottom (first match wins) for UNASSIGNED contacts.
 */

const CHANNEL_OPTIONS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'messenger', label: 'Messenger' },
  { value: 'instagram', label: 'Instagram' },
];

interface RuleRow {
  id: number;
  name: string;
  priority: number;
  isActive: boolean;
  strategy: string;
  assignedCount: number;
  agentIds: string[];
  condition: {
    channels?: string[];
    labelIds?: number[];
    keyword?: { mode: 'contains' | 'regex'; value: string };
  };
}

function RoutingSettingsPage() {
  const utils = trpc.useUtils();
  const settings = trpc.routing.getSettings.useQuery();
  const rules = trpc.routing.list.useQuery();
  const team = trpc.team.list.useQuery();
  const labels = trpc.contacts.labels.list.useQuery();

  const [editing, setEditing] = useState<RuleRow | 'new' | null>(null);

  const agentOptions = (team.data ?? [])
    .filter((m) => m.status === 'active' && m.user)
    .map((m) => ({
      value: m.user.id,
      label: m.user.displayName || m.user.email,
    }));
  const agentLabel = new Map(agentOptions.map((o) => [o.value, o.label]));
  const labelOptions = (labels.data ?? []).map((l) => ({ value: String(l.id), label: l.title }));

  const setSettings = trpc.routing.setSettings.useMutation({
    onSuccess: () => {
      void utils.routing.getSettings.invalidate();
      notifications.show({ message: 'Routing settings saved', color: 'green' });
    },
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });
  const remove = trpc.routing.delete.useMutation({
    onSuccess: () => void utils.routing.list.invalidate(),
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });
  const reorder = trpc.routing.reorder.useMutation({
    onSuccess: () => void utils.routing.list.invalidate(),
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });
  const toggleActive = trpc.routing.update.useMutation({
    onSuccess: () => void utils.routing.list.invalidate(),
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });

  const ruleList = (rules.data ?? []) as RuleRow[];

  const move = (index: number, delta: -1 | 1) => {
    const ids = ruleList.map((r) => r.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    const a = ids[index];
    const b = ids[target];
    if (a === undefined || b === undefined) return;
    ids[index] = b;
    ids[target] = a;
    reorder.mutate({ orderedIds: ids });
  };

  if (settings.isPending || rules.isPending) return <Loader />;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group gap="xs">
          <IconRoute size={22} />
          <Title order={2}>Team assignment</Title>
        </Group>
        <Text c="dimmed" size="sm">
          Automatically assign unassigned inbound conversations to your team. Rules run
          top-to-bottom — the first matching rule decides.
        </Text>

        <Card withBorder>
          <Stack gap="sm">
            <Switch
              label="Enable agent routing"
              checked={settings.data?.enabled ?? false}
              onChange={(e) =>
                setSettings.mutate({
                  enabled: e.currentTarget.checked,
                  pauseBotsWhenAssigned: settings.data?.pauseBotsWhenAssigned ?? false,
                })
              }
            />
            <Switch
              label="Pause bots when a human agent is assigned"
              description="Reply bots and the AI bot stay silent while a conversation is assigned."
              checked={settings.data?.pauseBotsWhenAssigned ?? false}
              onChange={(e) =>
                setSettings.mutate({
                  enabled: settings.data?.enabled ?? false,
                  pauseBotsWhenAssigned: e.currentTarget.checked,
                })
              }
            />
          </Stack>
        </Card>

        <Group justify="space-between">
          <Title order={4}>Rules</Title>
          <Button onClick={() => setEditing('new')}>New rule</Button>
        </Group>

        {ruleList.length === 0 && (
          <Card withBorder>
            <Text c="dimmed" size="sm">
              No rules yet. Add one — e.g. route WhatsApp messages containing &quot;pricing&quot;
              round-robin across your sales agents.
            </Text>
          </Card>
        )}

        <Stack gap="xs">
          {ruleList.map((rule, i) => (
            <Card key={rule.id} withBorder>
              <Group justify="space-between" wrap="nowrap">
                <Stack gap={4} style={{ minWidth: 0 }}>
                  <Group gap="xs">
                    <Text fw={600}>{rule.name}</Text>
                    <Badge color={rule.isActive ? 'green' : 'gray'} variant="light">
                      {rule.isActive ? 'Active' : 'Paused'}
                    </Badge>
                    <Badge variant="outline">{rule.strategy.replace('_', ' ')}</Badge>
                    <Badge variant="light" color="blue">
                      {rule.assignedCount} assigned
                    </Badge>
                  </Group>
                  <Text size="xs" c="dimmed">
                    {describeRule(rule, agentLabel)}
                  </Text>
                </Stack>
                <Group gap={4} wrap="nowrap">
                  <ActionIcon variant="subtle" onClick={() => move(i, -1)} disabled={i === 0}>
                    <IconArrowUp size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    onClick={() => move(i, 1)}
                    disabled={i === ruleList.length - 1}
                  >
                    <IconArrowDown size={16} />
                  </ActionIcon>
                  <Switch
                    size="xs"
                    checked={rule.isActive}
                    onChange={(e) =>
                      toggleActive.mutate({ ruleId: rule.id, isActive: e.currentTarget.checked })
                    }
                  />
                  <ActionIcon variant="subtle" onClick={() => setEditing(rule)}>
                    <IconPencil size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    onClick={() => {
                      if (confirm(`Delete rule "${rule.name}"?`))
                        remove.mutate({ ruleId: rule.id });
                    }}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Group>
            </Card>
          ))}
        </Stack>
      </Stack>

      {editing && (
        <RuleEditor
          initial={editing === 'new' ? null : editing}
          agentOptions={agentOptions}
          labelOptions={labelOptions}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void utils.routing.list.invalidate();
          }}
        />
      )}
    </motion.div>
  );
}

function describeRule(rule: RuleRow, agentLabel: Map<string, string>): string {
  const parts: string[] = [];
  if (rule.condition.channels?.length) parts.push(`channel: ${rule.condition.channels.join('/')}`);
  if (rule.condition.labelIds?.length) parts.push(`${rule.condition.labelIds.length} label(s)`);
  if (rule.condition.keyword) {
    parts.push(`keyword ${rule.condition.keyword.mode}: "${rule.condition.keyword.value}"`);
  }
  const cond = parts.length > 0 ? parts.join(' · ') : 'any message';
  const agents = rule.agentIds.map((id) => agentLabel.get(id) ?? '?').join(', ');
  return `${cond} → ${agents}`;
}

function RuleEditor({
  initial,
  agentOptions,
  labelOptions,
  onClose,
  onSaved,
}: {
  initial: RuleRow | null;
  agentOptions: Array<{ value: string; label: string }>;
  labelOptions: Array<{ value: string; label: string }>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [channels, setChannels] = useState<string[]>(initial?.condition.channels ?? []);
  const [labelIds, setLabelIds] = useState<string[]>(
    (initial?.condition.labelIds ?? []).map(String),
  );
  const [keywordMode, setKeywordMode] = useState<string>(
    initial?.condition.keyword?.mode ?? 'contains',
  );
  const [keywordValue, setKeywordValue] = useState(initial?.condition.keyword?.value ?? '');
  const [strategy, setStrategy] = useState(initial?.strategy ?? 'round_robin');
  const [agentIds, setAgentIds] = useState<string[]>(initial?.agentIds ?? []);
  const [regexError, setRegexError] = useState<string | null>(null);

  useEffect(() => {
    if (keywordMode === 'regex' && keywordValue) {
      try {
        new RegExp(keywordValue);
        setRegexError(null);
      } catch {
        setRegexError('Invalid regular expression');
      }
    } else {
      setRegexError(null);
    }
  }, [keywordMode, keywordValue]);

  const create = trpc.routing.create.useMutation({
    onSuccess: onSaved,
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });
  const update = trpc.routing.update.useMutation({
    onSuccess: onSaved,
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });

  const save = () => {
    const condition: Record<string, unknown> = {};
    if (channels.length > 0) condition.channels = channels;
    if (labelIds.length > 0) condition.labelIds = labelIds.map(Number);
    if (keywordValue.trim()) {
      condition.keyword = { mode: keywordMode, value: keywordValue.trim() };
    }
    const payload = {
      name: name.trim(),
      isActive: initial?.isActive ?? true,
      condition,
      strategy: strategy as 'specific' | 'round_robin' | 'least_open',
      agentIds,
    };
    if (initial) update.mutate({ ruleId: initial.id, ...payload });
    else create.mutate(payload);
  };

  const valid = name.trim().length > 0 && agentIds.length > 0 && !regexError;

  return (
    <Modal
      opened
      onClose={onClose}
      title={initial ? `Edit ${initial.name}` : 'New routing rule'}
      size="lg"
    >
      <Stack gap="sm">
        <TextInput
          label="Rule name"
          required
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <MultiSelect
          label="Channels"
          description="Leave empty to match every channel. Note: v1 evaluates WhatsApp inbound; Messenger/Instagram evaluation lands next."
          data={CHANNEL_OPTIONS}
          value={channels}
          onChange={setChannels}
          clearable
        />
        <MultiSelect
          label="Contact has any of these labels"
          description="Leave empty to skip the label check."
          data={labelOptions}
          value={labelIds}
          onChange={setLabelIds}
          clearable
          searchable
        />
        <Group grow align="flex-end">
          <Select
            label="Keyword match"
            data={[
              { value: 'contains', label: 'Message contains' },
              { value: 'regex', label: 'Message matches regex' },
            ]}
            value={keywordMode}
            onChange={(v) => setKeywordMode(v ?? 'contains')}
          />
          <TextInput
            label="Keyword / pattern (optional)"
            value={keywordValue}
            error={regexError}
            onChange={(e) => setKeywordValue(e.currentTarget.value)}
          />
        </Group>
        <div>
          <Text size="sm" fw={500} mb={4}>
            Assignment strategy
          </Text>
          <SegmentedControl
            fullWidth
            data={[
              { value: 'round_robin', label: 'Round robin' },
              { value: 'least_open', label: 'Least open chats' },
              { value: 'specific', label: 'Specific agent' },
            ]}
            value={strategy}
            onChange={setStrategy}
          />
        </div>
        <MultiSelect
          label={strategy === 'specific' ? 'Agent (first selected is used)' : 'Agents'}
          required
          data={agentOptions}
          value={agentIds}
          onChange={setAgentIds}
          searchable
        />
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} loading={create.isPending || update.isPending} disabled={!valid}>
            {initial ? 'Save changes' : 'Create rule'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export const Route = createFileRoute('/vendor/settings/routing')({
  component: RoutingSettingsPage,
});
